import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Trophy, AlertTriangle, MapPin, Crown, X, Check, ArrowLeft, Lock, Unlock, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * JourneyDashboard — modal that shows the teacher full performance for a journey:
 *  - class average accuracy
 *  - hardest missions (most errors)
 *  - per-student progress and accuracy
 *  - click on a student → opens the per-student map view (StudentJourneyMap)
 *  - "Fechar Jornada" toggle to release the gabarito to students
 */
export default function JourneyDashboard({ open, journeyId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [toggling, setToggling] = useState(false);

  const fetchData = async () => {
    if (!journeyId) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/teacher/journeys/${journeyId}/dashboard`, {
        credentials: 'include',
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Erro ao carregar painel');
      setData(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) fetchData(); /* eslint-disable-next-line */ }, [open, journeyId]);

  const handleToggleStatus = async () => {
    if (!data?.journey) return;
    const isClosed = data.journey.status === 'closed';
    const path = isClosed ? 'reopen' : 'close';
    setToggling(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/teacher/journeys/${journeyId}/${path}`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Erro');
      toast.success(d.message, { icon: isClosed ? '🔓' : '🔒' });
      await fetchData();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setToggling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="journey-dashboard">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500"/>
            Painel da Jornada
            {data?.journey && (
              <Badge className={data.journey.status === 'closed' ? 'bg-rose-500' : 'bg-emerald-500'}>
                {data.journey.status === 'closed' ? 'Encerrada' : 'Em andamento'}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-16 text-center"><Loader2 className="w-10 h-10 animate-spin mx-auto text-amber-500"/></div>
        ) : selectedStudent ? (
          <StudentJourneyDetailPanel
            journeyId={journeyId}
            studentId={selectedStudent}
            onBack={() => setSelectedStudent(null)}
          />
        ) : (
          <div className="space-y-4">
            {/* Title + close action */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-base font-outfit font-black text-slate-900">{data.journey.title}</p>
              <Button
                onClick={handleToggleStatus}
                disabled={toggling}
                className={data.journey.status === 'closed' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600'}
                data-testid="toggle-close-btn"
              >
                {toggling ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : (data.journey.status === 'closed' ? <Unlock className="w-4 h-4 mr-1"/> : <Lock className="w-4 h-4 mr-1"/>)}
                {data.journey.status === 'closed' ? 'Reabrir Jornada' : 'Fechar Jornada (liberar gabarito)'}
              </Button>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Alunos" value={data.journey.total_students} icon={<Users className="w-4 h-4"/>} color="violet"/>
              <StatCard label="Concluíram" value={`${data.finished_count}/${data.journey.total_students}`} icon={<Check className="w-4 h-4"/>} color="emerald"/>
              <StatCard label="Média (concluídos)" value={`${data.class_avg_accuracy}%`} icon={<Trophy className="w-4 h-4"/>} color="amber"/>
              <StatCard label="Média parcial" value={`${data.class_partial_accuracy}%`} icon={<Trophy className="w-4 h-4"/>} color="sky"/>
            </div>

            {/* Hardest missions */}
            {data.hardest_missions.some((m) => m.attempts > 0) && (
              <div className="rounded-2xl border-2 border-rose-200 bg-rose-50 p-3">
                <p className="text-xs font-outfit font-black uppercase tracking-widest text-rose-600 flex items-center gap-1 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5"/> Missões com mais erros
                </p>
                <ul className="space-y-1.5">
                  {data.hardest_missions.filter((m) => m.attempts > 0).slice(0, 3).map((m) => (
                    <li key={m.mission_id} className="flex items-center justify-between text-xs bg-white rounded-md p-2 border border-rose-100">
                      <span className="font-outfit font-black text-slate-800">Missão {m.order} — {m.title}</span>
                      <span className="text-rose-600 font-bold">{m.error_rate}% de erro <span className="text-slate-500 font-normal">({m.errors}/{m.attempts * (data.journey.num_missions ? 1 : 1)})</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Per-student list */}
            <div className="rounded-2xl border-2 border-slate-200 bg-white">
              <p className="px-3 py-2 text-xs font-outfit font-black uppercase tracking-widest text-slate-600 border-b border-slate-100">
                Alunos ({data.students.length})
              </p>
              <ul className="divide-y divide-slate-100">
                {data.students
                  .slice()
                  .sort((a, b) => (b.missions_done - a.missions_done) || (b.accuracy - a.accuracy))
                  .map((s) => (
                    <li key={s.student_id}>
                      <button
                        type="button"
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-left"
                        onClick={() => setSelectedStudent(s.student_id)}
                        data-testid={`dashboard-student-${s.student_id}`}
                      >
                        {/* Avatar */}
                        <div className="w-9 h-9 rounded-full bg-amber-100 border-2 border-slate-900 grid place-items-center font-outfit font-black text-slate-900 text-xs flex-shrink-0">
                          {s.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-outfit font-black text-sm text-slate-900 truncate">{s.name}</p>
                          <p className="text-[11px] text-slate-500">{s.missions_done}/{s.total_missions} missões · {s.correct}/{s.total_answered} acertos</p>
                        </div>
                        {/* Mini pin row */}
                        <div className="flex items-center gap-1">
                          {s.missions.map((m) => (
                            <div
                              key={m.mission_id}
                              title={m.title}
                              className={`w-2.5 h-2.5 rounded-full border ${
                                !m.completed ? 'bg-slate-200 border-slate-300' :
                                m.success    ? 'bg-emerald-500 border-emerald-700' :
                                               'bg-rose-500 border-rose-700'
                              }`}
                            />
                          ))}
                        </div>
                        <span className={`text-xs font-outfit font-black ${s.accuracy >= 75 ? 'text-emerald-600' : s.accuracy >= 50 ? 'text-amber-600' : s.missions_done > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                          {s.missions_done > 0 ? `${s.accuracy}%` : '—'}
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, icon, color }) {
  const colors = {
    violet:  'from-violet-50 to-violet-100 text-violet-700 border-violet-300',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-300',
    amber:   'from-amber-50 to-amber-100 text-amber-700 border-amber-300',
    sky:     'from-sky-50 to-sky-100 text-sky-700 border-sky-300',
  };
  return (
    <div className={`rounded-xl border-2 bg-gradient-to-br ${colors[color] || colors.violet} p-3`}>
      <div className="flex items-center gap-1 text-[10px] font-outfit font-black uppercase tracking-widest">
        {icon}{label}
      </div>
      <p className="text-xl font-outfit font-black mt-0.5">{value}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Per-student detail (map with green/red pins + drill-down answers)
 * ───────────────────────────────────────────────────────────────────── */
function StudentJourneyDetailPanel({ journeyId, studentId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openMissionIdx, setOpenMissionIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/teacher/journeys/${journeyId}/students/${studentId}`, {
          credentials: 'include',
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || 'Erro');
        setData(d);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [journeyId, studentId]);

  if (loading || !data) {
    return <div className="py-16 text-center"><Loader2 className="w-10 h-10 animate-spin mx-auto text-amber-500"/></div>;
  }

  const positioned = data.missions.map((m, i) => {
    const has = typeof m.map_x === 'number' && typeof m.map_y === 'number';
    if (has) return m;
    const t = data.missions.length > 1 ? i / (data.missions.length - 1) : 0.5;
    return { ...m, map_x: 15 + t * 70, map_y: 80 - Math.sin(t * Math.PI) * 55 };
  });

  return (
    <div className="space-y-3" data-testid="student-detail-panel">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="student-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1"/> Voltar
        </Button>
        <p className="text-base font-outfit font-black">{data.student.name}</p>
        <span className="text-xs text-slate-500">
          {data.progress.total_correct}/{data.progress.total_questions} acertos
          {data.progress.finalized && (
            <Badge className={`ml-2 ${data.progress.final_success ? 'bg-emerald-500' : 'bg-rose-500'}`}>
              {data.progress.final_success ? 'Vencida' : 'Fracassada'}
            </Badge>
          )}
        </span>
      </div>

      {/* Map */}
      <div
        className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden border-[3px] border-slate-900 shadow-[3px_3px_0_rgba(15,23,42,0.85)] bg-slate-200"
        style={{
          backgroundImage: `url(${data.journey.map_image_url || '/maps/default-quest-map.jpg'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 100">
          {positioned.length > 1 && (
            <polyline
              points={positioned.map((m) => `${m.map_x},${m.map_y}`).join(' ')}
              fill="none" stroke="#0f172a" strokeWidth="0.6" strokeDasharray="1.5 1.2"
              strokeLinecap="round" strokeLinejoin="round" opacity="0.85"
            />
          )}
        </svg>
        {positioned.map((m, i) => {
          const submitted = m.submitted;
          const ok = m.success;
          const bg = !submitted ? 'bg-slate-300' : ok ? 'bg-emerald-500' : 'bg-rose-500';
          return (
            <button
              key={m.mission_id}
              type="button"
              onClick={() => setOpenMissionIdx(i)}
              style={{ left: `${m.map_x}%`, top: `${m.map_y}%` }}
              className="absolute -translate-x-1/2 -translate-y-full hover:scale-110 transition-transform"
              data-testid={`student-map-pin-${i}`}
            >
              <div className="relative flex flex-col items-center">
                <div className={`rounded-full border-[3px] border-slate-900 shadow-[2px_2px_0_rgba(15,23,42,0.85)] grid place-items-center w-9 h-9 ${bg}`}>
                  {!submitted ? <Lock className="w-3.5 h-3.5 text-slate-700"/> :
                   ok ? <Check className="w-4 h-4 text-white"/> :
                        <X className="w-4 h-4 text-white"/>}
                </div>
                <span className="absolute -top-1 -left-1 w-4 h-4 bg-slate-900 text-white text-[9px] font-outfit font-black rounded-full grid place-items-center border-2 border-white">{m.order}</span>
                <span className="mt-1 px-1.5 py-0.5 rounded-md bg-white/95 border-2 border-slate-900 text-[10px] font-outfit font-black text-slate-900 whitespace-nowrap max-w-[140px] truncate shadow-sm">
                  {m.is_climax && <Crown className="inline w-3 h-3 text-amber-500 mr-0.5"/>}
                  {m.title}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Mission drill-down */}
      {openMissionIdx !== null && (
        <MissionAnswersPanel
          mission={data.missions[openMissionIdx]}
          onClose={() => setOpenMissionIdx(null)}
        />
      )}
    </div>
  );
}

function MissionAnswersPanel({ mission, onClose }) {
  const answersByIdx = Object.fromEntries((mission.answers || []).map((a) => [a.question_index, a]));
  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-3" data-testid="mission-answers-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="font-outfit font-black text-sm flex items-center gap-1">
          {mission.is_climax && <Crown className="w-4 h-4 text-amber-500"/>}
          Missão {mission.order} — {mission.title}
        </p>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4"/></Button>
      </div>
      {!mission.submitted ? (
        <p className="text-xs italic text-slate-500">Aluno ainda não submeteu esta missão.</p>
      ) : (
        <div className="space-y-2">
          {(mission.questions || []).map((q, qi) => {
            const ans = answersByIdx[qi] || {};
            const wasCorrect = !!ans.correct;
            return (
              <div key={qi} className={`rounded-lg p-2 border-2 text-xs ${wasCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
                <p className="font-semibold text-slate-800">{qi + 1}. {q.question_text}</p>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1 text-[11px]">
                  <p>
                    <span className="text-slate-500">Resposta do aluno: </span>
                    <span className={`font-bold ${wasCorrect ? 'text-emerald-700' : 'text-rose-700'}`}>{ans.answer || '—'}</span>
                  </p>
                  {q.question_type === 'multiple_choice' && (
                    <p>
                      <span className="text-slate-500">Correta: </span>
                      <span className="font-bold text-emerald-700">{q.correct_answer}</span>
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
